import {
	getEnvironmentStatus,
	listEnvironmentStatuses,
	startEnvironment,
	type EnvironmentInstanceSize,
	type EnvironmentStatus,
	type EnvironmentStatusEntry,
} from "@/lib/api";
import { isAbortError } from "@/lib/abort-error";

export const ENVIRONMENT_STATUS_POLL_INTERVAL_MS = 3000;
/**
 * 起動完了を待つ上限。バックエンドのスケジュール待ち (GPU サイズは専有 EC2 の起動込みで最長 12 分、
 * KubernetesSandboxOrchestrator.DedicatedNodeReadyTimeoutSeconds) + 起動処理
 * (最長 10 分、StartupTimeoutSeconds) を覆う長さにする。
 * インスタンス確保待ち (waitingForCapacity) はバックエンド側も無期限に待つため、その状態を観測する
 * たびに期限を延長する (ポーリングが先に諦めない)。
 */
export const ENVIRONMENT_STATUS_POLL_TIMEOUT_MS = 23 * 60 * 1000;
export const ENVIRONMENT_STATUS_POLL_MAX_FAILURES = 3;

interface EnvironmentPollingDependencies {
	getStatus: typeof getEnvironmentStatus;
	wait: (milliseconds: number, signal?: AbortSignal) => Promise<void>;
	now: () => number;
}

export interface EnvironmentPollingOptions {
	signal?: AbortSignal;
	intervalMs?: number;
	timeoutMs?: number;
	transitionalStatus?: EnvironmentStatus;
	onStatusChanged?: (entry: EnvironmentStatusEntry) => void;
	dependencies?: Partial<EnvironmentPollingDependencies>;
}

interface EnvironmentStatusesPollingDependencies {
	listStatuses: (signal?: AbortSignal) => Promise<EnvironmentStatusEntry[]>;
	wait: (milliseconds: number, signal?: AbortSignal) => Promise<void>;
	now: () => number;
}

export interface EnvironmentStatusesPollingOptions {
	signal?: AbortSignal;
	intervalMs?: number;
	timeoutMs?: number;
	onStatuses?: (entries: EnvironmentStatusEntry[]) => void;
	dependencies?: Partial<EnvironmentStatusesPollingDependencies>;
}

export interface EnsureEnvironmentRunningOptions extends EnvironmentPollingOptions {
	initialStatus?: EnvironmentStatusEntry;
	/** 起動が必要なときに使うインスタンスサイズ。省略時は環境に保存済みのサイズ。 */
	instanceSize?: EnvironmentInstanceSize;
	/** 起動が必要なときにデバイスの GPU を使うか (デバイスの環境だけ)。省略時は環境に保存済みの値。 */
	gpu?: boolean;
	start?: typeof startEnvironment;
}

function abortError(): DOMException {
	return new DOMException("処理を中止しました。", "AbortError");
}

function deadlineError(): Error {
	return new Error(
		"エージェントの起動確認がタイムアウトしました。しばらくしてから再度お試しください。",
	);
}

function deadlineSignal(parent: AbortSignal | undefined, milliseconds: number) {
	const controller = new AbortController();
	const onAbort = () => controller.abort(parent?.reason ?? abortError());
	if (parent?.aborted) onAbort();
	else parent?.addEventListener("abort", onAbort, { once: true });
	const timer = setTimeout(
		() =>
			controller.abort(
				new DOMException("エージェントの状態確認がタイムアウトしました。", "TimeoutError"),
			),
		Math.max(0, milliseconds),
	);
	return {
		signal: controller.signal,
		dispose: () => {
			clearTimeout(timer);
			parent?.removeEventListener("abort", onAbort);
		},
	};
}

function wait(milliseconds: number, signal?: AbortSignal): Promise<void> {
	return new Promise((resolve, reject) => {
		if (signal?.aborted) {
			reject(abortError());
			return;
		}
		const onAbort = () => {
			window.clearTimeout(timer);
			reject(abortError());
		};
		const timer = window.setTimeout(() => {
			signal?.removeEventListener("abort", onAbort);
			resolve();
		}, milliseconds);
		signal?.addEventListener("abort", onAbort, { once: true });
	});
}

function isTimeoutError(error: unknown): boolean {
	return error instanceof DOMException && error.name === "TimeoutError";
}

async function getInitialEnvironmentStatus(
	environmentId: string,
	options: EnvironmentPollingOptions,
): Promise<EnvironmentStatusEntry> {
	const getStatus = options.dependencies?.getStatus ?? getEnvironmentStatus;
	const waitForNext = options.dependencies?.wait ?? wait;
	const now = options.dependencies?.now ?? Date.now;
	const intervalMs = options.intervalMs ?? ENVIRONMENT_STATUS_POLL_INTERVAL_MS;
	const deadline = now() + (options.timeoutMs ?? ENVIRONMENT_STATUS_POLL_TIMEOUT_MS);
	let consecutiveFailures = 0;

	while (now() < deadline) {
		if (options.signal?.aborted) throw abortError();
		const request = deadlineSignal(options.signal, deadline - now());
		try {
			const entry = await getStatus(environmentId, request.signal);
			if (options.signal?.aborted) throw abortError();
			return entry;
		} catch (error) {
			if (isAbortError(error)) throw error;
			if (isTimeoutError(request.signal.reason)) throw deadlineError();
			if (now() >= deadline) throw deadlineError();
			consecutiveFailures++;
			if (consecutiveFailures >= ENVIRONMENT_STATUS_POLL_MAX_FAILURES) {
				throw new Error("エージェントの状態取得に連続して失敗しました。", { cause: error });
			}
			await waitForNext(Math.min(intervalMs, Math.max(0, deadline - now())), options.signal);
		} finally {
			request.dispose();
		}
	}

	throw new Error(
		"エージェントの状態確認がタイムアウトしました。しばらくしてから再度お試しください。",
	);
}

interface StatusPollingLoop<T> {
	/** 状態を 1 回取得する。渡されたシグナルは全体期限で中断される。 */
	fetchStatus: (signal: AbortSignal) => Promise<T>;
	/** この状態ならポーリングを終了できることを返す。 */
	isSettled: (status: T) => boolean;
	/** 取得のたびに呼び出す。`settled` は終了条件を満たしたかどうか。 */
	onStatus: (status: T, settled: boolean) => void;
	/** この状態を観測したら全体期限を延長する (無期限の待機を期限で打ち切らない)。 */
	extendsDeadline?: (status: T) => boolean;
	signal?: AbortSignal;
	intervalMs: number;
	timeoutMs: number;
	wait: (milliseconds: number, signal?: AbortSignal) => Promise<void>;
	now: () => number;
}

/**
 * 一定間隔で状態を取得し、終了条件を満たすまで待つ共通ループ。全体期限を過ぎた場合と、
 * 連続失敗が上限に達した場合は throw する。中断シグナルは AbortError を投げて抜ける。
 */
async function pollUntilSettled<T>(loop: StatusPollingLoop<T>): Promise<T> {
	const { signal, intervalMs, timeoutMs, now } = loop;
	let deadline = now() + timeoutMs;
	let consecutiveFailures = 0;

	while (now() < deadline) {
		if (signal?.aborted) throw abortError();
		await loop.wait(Math.min(intervalMs, Math.max(0, deadline - now())), signal);
		if (now() >= deadline) break;
		let status: T;
		const request = deadlineSignal(signal, deadline - now());
		try {
			status = await loop.fetchStatus(request.signal);
			if (signal?.aborted) throw abortError();
		} catch (error) {
			if (isAbortError(error)) throw error;
			if (isTimeoutError(request.signal.reason)) throw deadlineError();
			consecutiveFailures++;
			if (consecutiveFailures >= ENVIRONMENT_STATUS_POLL_MAX_FAILURES) {
				throw new Error("エージェントの状態取得に連続して失敗しました。", { cause: error });
			}
			continue;
		} finally {
			request.dispose();
		}
		consecutiveFailures = 0;
		if (loop.extendsDeadline?.(status)) deadline = now() + timeoutMs;
		const settled = loop.isSettled(status);
		loop.onStatus(status, settled);
		if (settled) return status;
	}

	throw deadlineError();
}

/** 環境が指定状態になるまで、期限と連続失敗上限を設けて状態を取得する。 */
export async function pollEnvironmentUntil(
	environmentId: string,
	isSettled: (status: EnvironmentStatus) => boolean,
	options: EnvironmentPollingOptions = {},
): Promise<EnvironmentStatusEntry> {
	const { transitionalStatus, onStatusChanged, dependencies } = options;
	const getStatus = dependencies?.getStatus ?? getEnvironmentStatus;

	return pollUntilSettled<EnvironmentStatusEntry>({
		fetchStatus: (requestSignal) => getStatus(environmentId, requestSignal),
		isSettled: (entry) => isSettled(entry.status),
		// 確保待ちは遷移中の一種だが、無期限に続くためユーザーに見せる価値がある。
		// transitionalStatus で上書きせずそのまま通知する。
		onStatus: (entry, settled) =>
			onStatusChanged?.(
				settled || !transitionalStatus || entry.status === "waitingForCapacity"
					? entry
					: { ...entry, status: transitionalStatus },
			),
		extendsDeadline: (entry) => entry.status === "waitingForCapacity",
		signal: options.signal,
		intervalMs: options.intervalMs ?? ENVIRONMENT_STATUS_POLL_INTERVAL_MS,
		timeoutMs: options.timeoutMs ?? ENVIRONMENT_STATUS_POLL_TIMEOUT_MS,
		wait: dependencies?.wait ?? wait,
		now: dependencies?.now ?? Date.now,
	});
}

/**
 * 自組織の環境状態の一覧を、指定条件を満たすまで一定間隔で取得する。
 * 単一環境ではなく「プロジェクト内のどれかが running になったか」のような
 * 横断的な条件を待つ用途に使う。
 */
export async function pollEnvironmentStatusesUntil(
	isSettled: (entries: EnvironmentStatusEntry[]) => boolean,
	options: EnvironmentStatusesPollingOptions = {},
): Promise<EnvironmentStatusEntry[]> {
	const { onStatuses, dependencies } = options;
	const listStatuses = dependencies?.listStatuses ?? listEnvironmentStatuses;

	return pollUntilSettled<EnvironmentStatusEntry[]>({
		fetchStatus: (requestSignal) => listStatuses(requestSignal),
		isSettled,
		onStatus: (entries) => onStatuses?.(entries),
		extendsDeadline: (entries) => entries.some((e) => e.status === "waitingForCapacity"),
		signal: options.signal,
		intervalMs: options.intervalMs ?? ENVIRONMENT_STATUS_POLL_INTERVAL_MS,
		timeoutMs: options.timeoutMs ?? ENVIRONMENT_STATUS_POLL_TIMEOUT_MS,
		wait: dependencies?.wait ?? wait,
		now: dependencies?.now ?? Date.now,
	});
}

function throwIfErrored(entry: EnvironmentStatusEntry): void {
	if (entry.status === "errored") {
		throw new Error(
			"エージェントに異常があります。エージェントを停止してから再度起動してください。",
		);
	}
}

function throwIfStartupFailed(entry: EnvironmentStatusEntry): void {
	if (entry.status !== "startupFailed") return;

	// 容量系の失敗は次の手が理由ごとに違うので、詳細を開かずとも分かる文言にする。
	throw new Error(startupFailureMessage(entry));
}

function startupFailureMessage({
	errorReason: reason,
	failureDetail,
}: EnvironmentStatusEntry): string {
	switch (reason) {
		case "unschedulable":
			return "キャパシティが不足しているため起動できません。稼働中のエージェントを停止するか、しばらく待ってから再度お試しください。";
		case "diskLimitExceeded":
			return "エージェントのディスク使用量が60 GiB以上のため起動できません。";
		case "diskSpaceInsufficient":
			return "保存先ディスクの空き容量が10 GiB未満のため起動できません。";
		case "deviceAccessDenied":
			return (
				failureDetail ??
				"選んだ USB 機器を開けないため起動できません。機器がデバイスに差してあるか確かめて、エージェントを起動し直してください。"
			);
		case "capacityUnavailable":
			return "選択したインスタンスサイズを確保できませんでした。別のサイズで起動するか、しばらく待ってから再度お試しください。";
		case "volumeExpansionRejected":
			return "このクラスタではボリュームを拡張できないため起動できません。管理者に連絡してください。";
		default:
			return "エージェントの起動に失敗しました。詳細を確認してから再度お試しください。";
	}
}

/**
 * サーバー側で進行する再起動 (restartEnvironment 要求済み) の完了を待つ。途中の
 * shuttingDown → notRunning → pending はサーバー側の再起動の途中経過なので終了条件にせず、
 * running で完了、errored / startupFailed はエラーとして投げる。
 */
export async function waitForEnvironmentRestart(
	environmentId: string,
	options: EnvironmentPollingOptions = {},
): Promise<EnvironmentStatusEntry> {
	const entry = await pollEnvironmentUntil(
		environmentId,
		(status) => status === "running" || status === "errored" || status === "startupFailed",
		options,
	);
	throwIfErrored(entry);
	throwIfStartupFailed(entry);
	return entry;
}

/** 必要に応じて環境を起動し、コマンドを受け付けられる running 状態まで待つ。 */
export async function ensureEnvironmentRunning(
	environmentId: string,
	options: EnsureEnvironmentRunningOptions = {},
): Promise<EnvironmentStatusEntry> {
	const now = options.dependencies?.now ?? Date.now;
	const deadline = now() + (options.timeoutMs ?? ENVIRONMENT_STATUS_POLL_TIMEOUT_MS);
	const remainingTimeout = () => {
		const remaining = deadline - now();
		if (remaining <= 0) {
			throw deadlineError();
		}
		return remaining;
	};
	const start = options.start ?? startEnvironment;
	let entry = options.initialStatus ?? (await getInitialEnvironmentStatus(environmentId, options));
	options.onStatusChanged?.(entry);
	if (entry.status === "running") return entry;
	throwIfErrored(entry);

	if (entry.status === "shuttingDown") {
		entry = await pollEnvironmentUntil(
			environmentId,
			(status) => status === "notRunning" || status === "errored" || status === "startupFailed",
			{
				...options,
				timeoutMs: remainingTimeout(),
				transitionalStatus: "shuttingDown",
			},
		);
		throwIfErrored(entry);
	}

	if (entry.status === "notRunning" || entry.status === "startupFailed") {
		const request = deadlineSignal(options.signal, remainingTimeout());
		try {
			await start(environmentId, {
				instanceSize: options.instanceSize,
				gpu: options.gpu,
				signal: request.signal,
			});
		} catch (error) {
			if (isTimeoutError(request.signal.reason)) {
				throw deadlineError();
			}
			throw error;
		} finally {
			request.dispose();
		}
		if (options.signal?.aborted) throw abortError();
		options.onStatusChanged?.({ environmentId, status: "pending" });
	}

	entry = await pollEnvironmentUntil(
		environmentId,
		(status) => status === "running" || status === "errored" || status === "startupFailed",
		{ ...options, timeoutMs: remainingTimeout(), transitionalStatus: "pending" },
	);
	throwIfErrored(entry);
	throwIfStartupFailed(entry);
	return entry;
}
